import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useSelectedOrganization } from "@/hooks/useCurrentUser";
import {
  mergeFanout,
  MAX_FANOUT,
  type FanoutMerge,
  type FanoutPart,
  type WallView,
} from "@/app/routes/dashboard/components/wallFanout";
import type {
  Reference,
  ReferenceListResponse,
  ReferenceSubmission,
  ReferenceSubmissionsResponse,
  ReferenceType,
  ReferencePlatform,
} from "@/types/reference.type";
import { isTerminalSubmission, submissionsRefetchInterval } from "./referencesPolling";
import {
  cancelReferenceFetches,
  patchReference,
  readReference,
  removeReferences,
} from "./referenceCache";

/**
 * Media URLs are presigned per response, so every refetch mints new ones and
 * remounts every <video>. The grid therefore never polls — freshness comes from
 * the submissions query invalidating it when work finishes.
 */
const REFERENCE_STALE_MS = 5 * 60_000;

export interface ReferenceFilters {
  /** A brand id narrows to its tagged rows; "global" shows only untagged ones. */
  brandId?: string;
  type?: ReferenceType;
  platform?: ReferencePlatform;
  /** With a brand id: that brand's references plus the ones saved for Everyone. */
  includeGlobal?: boolean;
  /** Only the references in this collection. */
  collectionId?: string;
  q?: string;
  page?: number;
  limit?: number;
}

export const referencesKey = (orgId: string | null, filters: ReferenceFilters) => [
  "references",
  orgId,
  filters,
];
export const submissionsKey = (orgId: string | null) => ["reference-submissions", orgId];

export function useOrgId(): string | null {
  const { selectedOrganization } = useSelectedOrganization();
  return selectedOrganization?.organization.id ?? null;
}

/* The two write payloads that carry more than an id, named so the fixture
   harness can stand in for the mutations without restating their shapes and
   drifting from them. Neither shape changed when they were named. */

/** The keys `v_UpdateBody` accepts on the update route. `userNote` "" clears
 *  the why; `suggestion` accepts M7's drafted why as the why, or dismisses it. */
export interface UpdateReferenceInput {
  referenceId: string;
  brandId?: string | null;
  title?: string;
  summary?: string;
  userNote?: string;
  suggestion?: "accept" | "dismiss";
}

export interface SubmitReferenceInput {
  /** "" with a trend: the trend itself is what gets saved. */
  input: string;
  brandId: string | null;
  /** Saves this trend as it is, as a "trend" reference, and lets the save survive a reload. */
  trendId?: string;
  /** Files uploaded with POST /uploads first; each becomes its own reference. */
  uploads?: { s3Key: string; fileName: string; mimeType: string }[];
}

/**
 * The one write a component needs from a mutation: start it, await it, and know
 * whether it is in flight. React Query's own result satisfies this structurally,
 * so production passes the hook straight through and nothing is wrapped.
 *
 * It exists for the dev harness. That route renders with no organization, so a
 * live mutation there would PATCH `/references/null/...` at the real API: a
 * request nobody asked for, answered with an error toast on every control the
 * page has. The harness hands its own five in instead and answers them from its
 * fixture list.
 */
export interface WriteHandle<TVars> {
  mutate: (vars: TVars) => void;
  mutateAsync: (vars: TVars) => Promise<unknown>;
  isPending: boolean;
}

/** Every write this surface can perform, in one bag, so a caller either supplies
 *  all of them or none and there is no half-stubbed page. */
export interface ReferenceWrites {
  update: WriteHandle<UpdateReferenceInput>;
  remove: WriteHandle<string>;
  submit: WriteHandle<SubmitReferenceInput>;
  retry: WriteHandle<string>;
  dismiss: WriteHandle<string>;
}

/** What the wall reads. Stated as a type rather than left inline so the fixture
 *  harness can hand the page the same shape without casting it. */
export interface ReferenceListState {
  references: Reference[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
}

/** One filter set, one URL. Extracted so the single-request path and every
 *  constituent of a fan-out build the same query string from the same rules. */
async function fetchReferenceList(
  orgId: string | null,
  filters: ReferenceFilters,
): Promise<ReferenceListResponse> {
  const params = new URLSearchParams();
  params.set("page", String(filters.page ?? 1));
  params.set("limit", String(filters.limit ?? 24));
  if (filters.brandId) params.set("brandId", filters.brandId);
  if (filters.type) params.set("type", filters.type);
  if (filters.platform) params.set("platform", filters.platform);
  if (filters.includeGlobal) params.set("includeGlobal", "true");
  if (filters.collectionId) params.set("collectionId", filters.collectionId);
  if (filters.q) params.set("q", filters.q);
  const res = await axiosInstance.get<ReferenceListResponse>(
    `/references/${orgId}?${params.toString()}`,
  );
  return res.data;
}

export function useReferences(
  filters: ReferenceFilters,
  /** `enabled: false` switches the live read off at the source, which is how the
   *  dev fixture harness renders this surface without a backend. */
  opts?: { enabled?: boolean },
): ReferenceListState {
  const orgId = useOrgId();

  const query = useQuery({
    queryKey: referencesKey(orgId, filters),
    queryFn: () => fetchReferenceList(orgId, filters),
    enabled: !!orgId && (opts?.enabled ?? true),
    staleTime: REFERENCE_STALE_MS,
    placeholderData: keepPreviousData,
  });

  return {
    references: query.data?.references ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

/** The wall's single-request read, one page at a time as the viewer scrolls. */
export interface ReferencePagedState extends ReferenceListState {
  hasMore: boolean;
  isFetchingMore: boolean;
  loadMore: () => void;
}

/**
 * The wall's single-request path as an infinite list: page N+1 is asked when the
 * viewer nears the bottom, and the pages read so far are drawn as one wall.
 *
 * Its key carries a "pages" segment so it never shares a cache entry with the
 * one-page `useReferences` reads (different data shapes), while the
 * `["references", orgId]` prefix every write invalidates still reaches it.
 */
export function useReferencesInfinite(
  /** `page` is ignored: the query owns which pages are read. */
  filters: ReferenceFilters,
  opts?: { enabled?: boolean },
): ReferencePagedState {
  const orgId = useOrgId();
  const limit = filters.limit ?? 24;

  const query = useInfiniteQuery({
    queryKey: ["references", orgId, "pages", filters],
    queryFn: ({ pageParam }) => fetchReferenceList(orgId, { ...filters, page: pageParam, limit }),
    initialPageParam: 1,
    getNextPageParam: (last, pages) =>
      pages.length * limit < last.total ? pages.length + 1 : undefined,
    enabled: !!orgId && (opts?.enabled ?? true),
    staleTime: REFERENCE_STALE_MS,
    placeholderData: keepPreviousData,
  });

  /* Newest first over offsets: a reference saved mid-scroll shifts every later
     page by one, so the next page can repeat the last row of the one before. */
  const references = useMemo(() => {
    const seen = new Set<string>();
    return (query.data?.pages ?? [])
      .flatMap((page) => page.references)
      .filter((reference) => !seen.has(reference.id) && seen.add(reference.id));
  }, [query.data]);

  return {
    references,
    total: query.data?.pages[0]?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching && !query.isFetchingNextPage,
    isError: query.isError,
    refetch: () => void query.refetch(),
    hasMore: query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}

/* ------------------------------------------------------------- the fan-out */

/**
 * The window ONE fan-out request reads.
 *
 * `v_ListQuery` caps `limit` at `MAX_PAGE_SIZE` = 60 (ReferenceController.ts),
 * so this is the widest single answer the route can give and the most one picked
 * kind can contribute to a merged wall. A kind whose library runs past it is
 * read short, and the merged count is reported as a floor rather than as a total
 * nobody read.
 */
export const REFERENCE_FANOUT_WINDOW = 60;

export interface ReferenceWallPlan {
  /** One entry per request. Exactly one entry IS the single-request path: the
   *  page the viewer is on, paginated and counted by the route. */
  requests: ReferenceFilters[];
  /** The plan was cut at MAX_FANOUT and some picked kind was never asked. */
  capped: boolean;
}

/**
 * What the wall will ask, given what is picked.
 *
 * Kind is the only axis on this wall that holds a set — Source and Scope are
 * facets over one parameter each — so the plan is one request per picked kind
 * and there is no cross product to take. Six kinds is under MAX_FANOUT, so the
 * cut below is unreachable from today's controls.
 *
 * No picked kind at all is one request with no `type`, which is the unfiltered
 * wall; one picked kind is one request with `type` sent server-side, unchanged.
 */
export function referenceWallPlan(
  base: Omit<ReferenceFilters, "type" | "page" | "limit">,
  types: readonly ReferenceType[],
  page: number,
  limit: number,
): ReferenceWallPlan {
  if (types.length <= 1) {
    return { requests: [{ ...base, type: types[0], page, limit }], capped: false };
  }

  const kept = types.slice(0, MAX_FANOUT);
  return {
    requests: kept.map((type) => ({
      ...base,
      type,
      /* Every constituent reads its OWN first window. Their pages cannot be
         lined up with each other, so the merged set is paged on the client. */
      page: 1,
      limit: REFERENCE_FANOUT_WINDOW,
    })),
    capped: kept.length < types.length,
  };
}

const referenceId = (reference: Reference) => reference.id;
const referenceTime = (reference: Reference) => Date.parse(reference.createdAt);

/** One answered list in the shape the merge takes, so the live hook and the dev
 *  fixture harness feed the SAME merge instead of two copies of it. */
export function referenceFanoutPart(
  list: ReferenceListState,
  request: ReferenceFilters,
): FanoutPart<Reference> {
  const answered = !list.isLoading && !list.isError;
  return {
    rows: list.references,
    total: answered ? list.total : null,
    truncated: answered && list.total > (request.limit ?? REFERENCE_FANOUT_WINDOW),
    failed: list.isError,
    pending: list.isLoading,
    fetching: list.isFetching,
    refetch: list.refetch,
  };
}

/**
 * One request per picked kind, in parallel, merged into one wall.
 *
 * `useQueries` rather than a single query over the whole plan, so each picked
 * kind keeps its OWN cache entry and switching {Video, Image} to {Video, Post}
 * reuses the Video answer.
 *
 * No `placeholderData` here, unlike the single-request path: a placeholder
 * inside a merge is one kind's stale rows sitting beside another's fresh ones,
 * which is the half-merged wall the merge exists never to draw.
 */
export function useReferencesFanout(
  requests: readonly ReferenceFilters[],
  opts: { enabled: boolean; capped: boolean },
): FanoutMerge<Reference> {
  const orgId = useOrgId();

  const results = useQueries({
    queries: requests.map((filters) => ({
      queryKey: referencesKey(orgId, filters),
      enabled: !!orgId && opts.enabled,
      queryFn: () => fetchReferenceList(orgId, filters),
      staleTime: REFERENCE_STALE_MS,
    })),
  });

  const parts: FanoutPart<Reference>[] = results.map((result, index) => ({
    rows: result.data?.references ?? [],
    total: result.data?.total ?? null,
    truncated: result.data
      ? result.data.total > (requests[index].limit ?? REFERENCE_FANOUT_WINDOW)
      : false,
    failed: result.isError,
    pending: result.isPending,
    fetching: result.isFetching,
    refetch: () => {
      void result.refetch();
    },
  }));

  return mergeFanout(parts, referenceId, referenceTime, opts.capped);
}

/** The route's own answer as the wall reads it. The list route sends no page
 *  count, so it is arithmetic over the total it does send — which is a
 *  library-wide figure, never a floor. */
export function referenceSingleView(
  list: ReferenceListState,
  limit: number,
): WallView<Reference> {
  return {
    rows: list.references,
    pageCount: Math.max(1, Math.ceil(list.total / limit)),
    count: list.total,
    atLeast: false,
    isLoading: list.isLoading,
    isFetching: list.isFetching,
    isError: list.isError,
    failed: 0,
    requested: 1,
    refetch: list.refetch,
  };
}

/**
 * The activity feed. Also the grid's freshness signal: when a submission reaches
 * a terminal state, whatever it produced is now visible, so the list is
 * invalidated exactly once rather than on a timer.
 */
export function useReferenceSubmissions(opts?: { enabled?: boolean }) {
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: submissionsKey(orgId),
    queryFn: async () => {
      const res = await axiosInstance.get<ReferenceSubmissionsResponse>(
        `/references/${orgId}/submissions`,
      );
      return res.data.submissions;
    },
    enabled: !!orgId && (opts?.enabled ?? true),
    refetchInterval: (query) => submissionsRefetchInterval(query.state.data),
    refetchIntervalInBackground: true,
  });

  const workingIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    const submissions = query.data;
    if (!submissions) return;

    const stillWorking = new Set(
      submissions.filter((s) => !isTerminalSubmission(s)).map((s) => s.id),
    );
    const finished = [...workingIds.current].filter((id) => !stillWorking.has(id));
    workingIds.current = stillWorking;

    if (finished.length > 0) {
      void queryClient.invalidateQueries({ queryKey: ["references", orgId] });
    }
  }, [query.data, orgId, queryClient]);

  return {
    submissions: query.data ?? [],
    isLoading: query.isLoading,
  };
}

/** The submission route, called from one place. The composer's mutation below
 *  and the Trends wall's Save both file through it, so the two can never
 *  disagree about the route or the body it takes. */
export async function postReferenceSubmission(
  orgId: string | null,
  input: SubmitReferenceInput,
): Promise<ReferenceSubmission> {
  const res = await axiosInstance.post<ReferenceSubmission>(`/references/${orgId}`, input);
  return res.data;
}

export function useSubmitReference() {
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SubmitReferenceInput) => postReferenceSubmission(orgId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: submissionsKey(orgId) });
    },
  });
}

export function useRetrySubmission() {
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (submissionId: string) => {
      const res = await axiosInstance.post<ReferenceSubmission>(
        `/references/${orgId}/submissions/${submissionId}/retry`,
      );
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: submissionsKey(orgId) });
    },
  });
}

export function useDismissSubmission() {
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (submissionId: string) => {
      await axiosInstance.delete(`/references/${orgId}/submissions/${submissionId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: submissionsKey(orgId) });
    },
  });
}

/** Whether a wall read with these filters still shows this reference. */
function wallShows(filters: ReferenceFilters | undefined, reference: Reference) {
  if (!filters?.brandId) return true;
  if (filters.brandId === "global") return reference.brandId === null;
  return reference.brandId === filters.brandId || (!!filters.includeGlobal && reference.brandId === null);
}

const filtersOf = (key: readonly unknown[]) =>
  key.find((part): part is ReferenceFilters => !!part && typeof part === "object" && !Array.isArray(part));

export function useUpdateReference() {
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateReferenceInput) => {
      const { referenceId, ...body } = input;
      const res = await axiosInstance.patch<Reference>(
        `/references/${orgId}/${referenceId}`,
        body,
      );
      return res.data;
    },
    /* Optimistic: the why, the brand and M7's suggestion change on screen at
       once. A move to another brand takes it off every wall that no longer
       shows it. A refusal puts back only this reference. */
    onMutate: async ({ referenceId, suggestion, ...patch }) => {
      await cancelReferenceFetches(queryClient, orgId);
      const before = readReference(queryClient, orgId, referenceId);
      if (!before) return { before };
      const next: Reference = { ...before };
      if (patch.title !== undefined) next.title = patch.title;
      if (patch.summary !== undefined) next.summary = patch.summary;
      if (patch.userNote !== undefined) next.userNote = patch.userNote.trim() || null;
      if (patch.brandId !== undefined) next.brandId = patch.brandId;
      if (suggestion === "accept") {
        next.userNote = before.suggestedNote ?? before.userNote;
        next.suggestedNote = null;
      } else if (suggestion === "dismiss") next.suggestedNote = null;
      patchReference(queryClient, orgId, referenceId, () => next);
      if (patch.brandId !== undefined && patch.brandId !== before.brandId) {
        removeReferences(queryClient, orgId, [referenceId], (key) => !wallShows(filtersOf(key), next));
      }
      return { before };
    },
    onError: (error, _input, context) => {
      if (context?.before) patchReference(queryClient, orgId, context.before.id, () => context.before!);
      queue.add({
        title: "Could not save that",
        description: getAiRequestErrorMessage(error, "Please try again."),
        variant: "error",
      });
      void queryClient.invalidateQueries({ queryKey: ["references", orgId] });
    },
    onSettled: (_data, _error, input) => {
      if (input.brandId !== undefined) {
        void queryClient.invalidateQueries({ queryKey: ["references", orgId] });
        void queryClient.invalidateQueries({ queryKey: ["reference-collections", orgId] });
      }
    },
  });
}

export function useDeleteReference() {
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["reference-delete", orgId],
    mutationFn: async (referenceId: string) => {
      await axiosInstance.delete(`/references/${orgId}/${referenceId}`);
    },
    /* Optimistic: it leaves every wall on confirm. A refused delete reads the
       walls again, which puts it back where it was, and says so. */
    onMutate: async (referenceId) => {
      await cancelReferenceFetches(queryClient, orgId);
      removeReferences(queryClient, orgId, [referenceId]);
    },
    onError: () => {
      queue.add({
        title: "Could not delete",
        description: "Please try again. The reference is still in the library.",
        variant: "error",
      });
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: ["reference-delete", orgId] }) > 1) return;
      void queryClient.invalidateQueries({ queryKey: ["references", orgId] });
      void queryClient.invalidateQueries({ queryKey: ["reference-collections", orgId] });
    },
  });
}

/**
 * The detail read: the same row plus every carousel slide with a fresh link.
 * Only asked for a carousel, the one thing the wall row does not carry; the
 * sheet draws the wall's row meanwhile, so it never waits on this.
 */
export function useReferenceDetail(referenceId: string | null, opts?: { enabled?: boolean }) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ["reference-detail", orgId, referenceId],
    queryFn: async () => {
      const res = await axiosInstance.get<Reference>(`/references/${orgId}/${referenceId}`);
      return res.data;
    },
    enabled: !!orgId && !!referenceId && (opts?.enabled ?? true),
    staleTime: REFERENCE_STALE_MS,
  });
}
